import { channelClient, RequestPolicies, type ChannelUnsubscribe } from 'shared/channels';
import { DomainMessagesTypes } from 'shared/domain/messages/message';
import type { FailableUserDto, UserWithPermissionsInDto } from 'shared/domain/user/types/inDto';
import type { SuperadminUserUpdateDto } from 'shared/domain/user/types/outDto';
import { isUserWithPermissionsInDto } from 'shared/domain/user/types/userInDtoGuards';
import type { CurrentUserIdentity } from './currentUserIdentity';
import { currentUserContract, type PersonalDataChange } from './currentUserContract';

/** The logged-in user as served by the service worker. */
export const currentUserClient = {
  /** Downloads the user with its permissions; rejects when the server refused. */
  refresh(): Promise<UserWithPermissionsInDto> {
    return channelClient.request(currentUserContract, 'refresh', undefined, RequestPolicies.read);
  },

  updatePersonalData(change: PersonalDataChange): Promise<FailableUserDto> {
    return channelClient.request(currentUserContract, 'updatePersonalData', change, RequestPolicies.mutation);
  },

  updateSuperadminLabel(change: SuperadminUserUpdateDto): Promise<FailableUserDto> {
    return channelClient.request(currentUserContract, 'updateSuperadminLabel', change, RequestPolicies.mutation);
  },

  /** Tells the worker who is logged in, for its logs. */
  identify(identity: CurrentUserIdentity): void {
    channelClient.notify(currentUserContract, 'identify', identity);
  },

  /** The logged-in user with the permissions the worker downloaded, each time it downloads them. */
  onPermissionsChanged(handler: (userData: UserWithPermissionsInDto) => void): ChannelUnsubscribe {
    return channelClient.onEventOfType(currentUserContract, DomainMessagesTypes.currentUserPermissions, ({ data }) => {
      if (isUserWithPermissionsInDto(data?.userData)) {
        handler(data.userData);
      }
    });
  },
};
